import { AbstractRelay } from '@nostr/tools/abstract-relay'
import { loadNostrUser } from '@nostr/gadgets/metadata'
import { loadFollowsList, loadRelayList } from '@nostr/gadgets/lists'
import { pool } from '@nostr/gadgets/global'
import { store } from './services/store.service'
import { current, outbox } from './services/outbox.service'
import client from './services/client.service'
import { Filter } from '@nostr/tools/filter'
import * as kinds from '@nostr/tools/kinds'
import { HallwayUniverse } from './types/window'

function mergeUniverse<T extends Record<string, any>>(
  defaults: T,
  user: Partial<T> | undefined
): T {
  const out = { ...defaults }
  if (user) {
    for (const key of Object.keys(out) as (keyof T)[]) {
      if (user[key] !== undefined) {
        out[key] = user[key]!
      }
    }
  }
  return out
}

const universeDefaults: HallwayUniverse = {
  indexerUrls: [
    'wss://purplepag.es',
    'wss://indexer.coracle.social',
    'wss://indexer.nostrarchives.com',
    'wss://user.kindpag.es'
  ],
  hardcodedFeeds: [
    {
      name: '140 characters',
      icon: 'https://c.nostr.media/037/584/037584886e7e3c653ab7a29f6891bc57937dac5ff03cad0025220e3710694aa0.png',
      relays: ['wss://140.f7z.io/']
    },
    { relays: ['wss://relays.land/spatianostra'] },
    { name: 'Latin', relays: ['wss://lang.relays.land/la'] },
    { relays: ['wss://pyramid.fiatjaf.com/uppermost'] },
    { name: 'Trending', relays: ['wss://feeds.nostrarchives.com/notes/trending/reposts/today'] },
    { relays: ['wss://spatia-arcana.com/nox'] },
    {
      name: 'Gigi and whales',
      relays: ['wss://topic.relays.land/whales'],
      pubkeys: ['6e468422dfb74a5738702a8823b9b28168abab8655faacb6853cd0ee15deee93']
    }
  ],
  defaultFavoriteRelays: [
    'wss://theforest.nostr1.com/',
    'wss://relays.land/spatianostra/',
    'wss://pyramid.fiatjaf.com/popular',
    'wss://spatia-arcana.com/lux',
    'wss://news.utxo.one/'
  ],
  defaultBlossomServers: [
    'https://blossom.primal.net/',
    'https://nostr.media/',
    'https://nostr.download/',
    'https://blossom.band/'
  ],
  defaultInbox: ['wss://nostr.mom', 'wss://nostr.mom', 'wss://inbox.relays.land'],
  defaultOutbox: ['wss://nos.lol', 'wss://offchain.pub', 'wss://relay.wisp.talk'],
  baseShareURL: 'https://njump.me/',
  clientHandlers: {
    byKind: {
      [kinds.LongFormArticle]: [
        { name: 'Habla', urlPattern: 'https://habla.coracle.social/a/{}' },
        { name: 'Yakihonne', urlPattern: 'https://yakihonne.com/{}' }
      ],
      [kinds.LiveEvent]: [{ name: 'zap.stream', urlPattern: 'https://zap.stream/{}' }],
      [kinds.GroupMetadata]: [
        { name: 'Nostrord', urlPattern: 'https://web.nostrord.com/?relay={hint}&group={d}' }
      ],
      [kinds.Date]: [{ name: 'Coracle', urlPattern: 'https://coracle.social/{}' }],
      [kinds.Time]: [{ name: 'Coracle', urlPattern: 'https://coracle.social/{}' }]
    },
    fallback: [
      {
        name: 'Grimoire',
        urlPattern: 'https://grimoire2.netlify.app/run?cmd=open%20{}'
      },
      {
        name: 'njump',
        urlPattern: 'https://njump.me/{}'
      }
    ]
  },
  defaultNostrConnectRelays: ['wss://bucket.coracle.social/', 'wss://relay.primal.net/'],
  bigRelayUrls: ['wss://relay.damus.io/', 'wss://relay.primal.net/', 'wss://nos.lol/'],
  noteSearchRelayUrls: ['wss://search.nostrarchives.com/'],
  noteSearchRelayUrlsExtra: ['wss://antiprimal.net', 'wss://nostr.wine', 'wss://search.nos.today'],
  profileSearchRelayUrls: [
    'wss://antiprimal.net',
    'wss://search.nostrarchives.com/',
    'wss://nostr.wine'
  ],
  registryOfKindsURL:
    'https://cdn.jsdelivr.net/gh/nostr-protocol/registry-of-kinds@master/schema.yaml',
  appName: 'Hallway',
  groupedTimeframe: 60 * 60 * 24,
  groupedDefault: false,
  defaultPrimaryColor: 'TEAL',
  defaultThemeSetting: 'system',
  includeRepliesDefault: true,
  hideNotificationsDefault: false,
  hideConversationsDefault: false,
  hideFollowingDefault: false,
  notificationListStyleDefault: 'compact',
  noteListStyleDefault: 'full',
  sidebarCollapseDefault: false,
  enableSingleColumnLayoutDefault: false,
  readRepliesFrom: [],
  readRepliesFromExclusive: false,
  imgproxyUrl: 'http://localhost:3002/imgproxy/',
  dearrowYoutube: true,
  linkPreviewService: 'https://dufflepud.onrender.com',
  genericCorsProxy: 'https://cors-proxy.jumble.social/sites/',
  feedKinds: [
    kinds.ShortTextNote,
    kinds.Repost,
    kinds.Photo,
    kinds.NormalVideo,
    kinds.ShortVideo,
    kinds.Comment,
    kinds.Voice,
    kinds.VoiceComment,
    kinds.Highlights,
    kinds.LongFormArticle,
    kinds.WikiArticle,
    kinds.WebBookmarks,
    kinds.PodcastEpisode
  ],
  kindGroups: [
    { kinds: [kinds.Voice, kinds.ShortTextNote, kinds.Repost], label: 'Notes' },
    { kinds: [kinds.Comment, kinds.VoiceComment], label: 'Comments' },
    { kinds: [kinds.Highlights], label: 'Highlights' },
    { kinds: [kinds.WikiArticle], label: 'Wikis' },
    { kinds: [kinds.LongFormArticle], label: 'Articles', disabled: true },
    {
      kinds: [kinds.Photo, kinds.NormalVideo, kinds.ShortVideo, kinds.PodcastEpisode],
      label: 'Media',
      disabled: true
    }
  ],
  homeMarkdown:
    '# Hallway\n\n' +
    'This is a demo of **Hallway**, a Nostr client made to be customized for communities.\n\n' +
    'This section can be filled with anything you want.',
  pomegranateCentral: 'https://auth.njump.me',
  pomegranateOperators: [
    'https://po.njump.me',
    'https://po.nostrver.se',
    'https://po.f7z.io',
    'https://po.jumble.social',
    'https://po.coracle.social'
  ],
  avatarStyleDefault: 'square'
} satisfies HallwayUniverse

let lastSyncTime: string | null = null
current.onsync.push((pubkey?: string) => {
  if (!pubkey) return
  lastSyncTime = new Date().toISOString()
})
window.hallway = {
  ...window.hallway,
  universe: mergeUniverse(
    universeDefaults,
    window.hallway?.universe as Partial<HallwayUniverse> | undefined
  )
}

window.hallway = {
  ...window.hallway,
  store,
  client,
  relays() {
    const relays = Array.from((pool as any).relays.values()) as AbstractRelay[]

    const connected = relays.filter((r) => r.connected).length
    const withSubs = relays.filter((r) => r.openSubs.size > 0).length

    const subsPerRelay = relays.map((r) => ({
      url: r.url.substring(0, 30),
      connected: r.connected,
      subs: r.openSubs.size
    }))

    console.log(
      `relay connections: ${connected}/${relays.length} connected, ${withSubs} with active subs`
    )
    console.table(subsPerRelay)
  },
  async timestamps() {
    if (!this.account) {
      console.log('no account connected')
      return
    }

    const followings = (await loadFollowsList(this.account)).items
    const currentPubkeys = [this.account, ...followings]
    const tableData: any[] = []

    const bounds = await store.getOutboxBounds()

    await Promise.all(
      currentPubkeys.map(async (pubkey) => {
        const latestEvent = await store.queryEvents({ authors: [pubkey], kinds: [1] }, 1)
        const meta = await loadNostrUser(pubkey)
        if (meta) client.addUsernameToIndex(meta)
        const row = {
          name: meta.shortName,
          latestEvent:
            latestEvent.length > 0
              ? new Date(latestEvent[0].created_at * 1000).toISOString()
              : null,
          boundStart: bounds[pubkey]
            ? new Date(bounds[pubkey]?.['1']?.[0] * 1000).toISOString()
            : null,
          boundEnd: bounds[pubkey]
            ? new Date(bounds[pubkey]?.['1']?.[1] * 1000).toISOString()
            : null
        }
        tableData.push(row)
      })
    )

    console.log('current outbox database timestamps')
    console.table(tableData)
  },
  async state() {
    if (!this.account) {
      console.log('no account connected')
      return
    }

    const followings = (await loadFollowsList(this.account)).items
    const currentPubkeys = [this.account, ...followings]

    const currentlySyncing = (outbox as any).currentlySyncing as Map<string, () => void>
    const permanentlyLive = (outbox as any).permanentlyLive as Set<string>

    const liveSubs = (outbox as any).liveSubscriptions as { url: string; filter: Filter }[]
    console.log('liveSubs', liveSubs)

    const tableData: any[] = []

    await Promise.all(
      currentPubkeys.map(async (pubkey) => {
        const meta = await loadNostrUser(pubkey)
        if (meta) client.addUsernameToIndex(meta)
        const relays: string[] = []
        for (const { url, filter } of liveSubs) {
          for (const pk of filter.authors || []) {
            if (pk === pubkey) {
              relays.push(url)
              break
            }
          }
        }

        const row = {
          name: meta.shortName,
          syncing: currentlySyncing.has(pubkey),
          lastSync: lastSyncTime,
          relays: relays.join(' '),
          live: permanentlyLive.has(pubkey)
        }
        tableData.push(row)
      })
    )

    console.log('current outbox syncing status')
    console.table(tableData)
  },
  loadNostrUser,
  loadRelayList,
  loadFollowsList
}
